import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { PlusIcon, XIcon } from "lucide-react";

import {
  BaseForm,
  FormInput,
  FormSection,
  FormTextarea,
  FormToggle,
} from "components/BaseForm";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import RevenueOpportunityService from "services/RevenueOpportunities";

const IN_USE_MESSAGE = "In use by a Revenue Opportunity and cannot be removed";

// Every child row adds a description, so the dialog can outgrow the viewport;
// the centered DialogContent then pushes its Close and Save buttons off-screen.
export const STATUS_DIALOG_CLASS_NAME =
  "tw:max-h-[calc(100vh-2rem)] tw:overflow-y-auto";

const statusFlagSchema = {
  is_closed: z.boolean(),
  is_won: z.boolean(),
};

const descriptionSchema = z.string().trim().min(1, "Description is required");

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  description: descriptionSchema,
  ...statusFlagSchema,
  children: z.array(
    z.object({
      id: z.number().optional(),
      name: z.string().min(1, "Name is required"),
      description: descriptionSchema,
      in_use: z.boolean().optional(),
      ...statusFlagSchema,
    })
  ),
});

// `in_use` is read-only context for the form, so it is left out of the payload.
const toChildPayload = child => ({
  ...(child.id ? { id: child.id } : {}),
  name: child.name,
  description: child.description,
  is_closed: child.is_closed,
  is_won: child.is_won,
});

const StatusFlagToggles = ({ namePrefix }) => {
  const { control, setValue } = useFormContext();
  const closedName = `${namePrefix}is_closed`;
  const wonName = `${namePrefix}is_won`;
  return (
    <Controller
      name={closedName}
      control={control}
      render={({ field }) => (
        <>
          <Field>
            <div className="tw:flex tw:items-center tw:gap-2">
              <Switch
                id={closedName}
                checked={!!field.value}
                onBlur={field.onBlur}
                onCheckedChange={checked => {
                  field.onChange(!!checked);
                  if (!checked) {
                    setValue(wonName, false);
                  }
                }}
              />
              <FieldLabel htmlFor={closedName}>Is Closed</FieldLabel>
            </div>
          </Field>
          {field.value && <FormToggle name={wonName} label="Is Won" />}
        </>
      )}
    />
  );
};

/**
 * A child a Revenue Opportunity still points at cannot be removed, so the
 * control is marked `aria-disabled` rather than `disabled`: the Button's
 * disabled styles include `pointer-events-none`, which would stop the tooltip
 * explaining why from ever opening. The server-side guard remains the
 * authority, since `inUse` is only accurate as of the form's last fetch.
 */
const RemoveChildButton = ({ inUse, onRemove }) => {
  const button = (
    <Button
      type="button"
      variant="destructive"
      size="icon-sm"
      aria-label="Remove child"
      aria-disabled={inUse || undefined}
      className={cn(inUse && "tw:opacity-50 tw:cursor-not-allowed")}
      onClick={inUse ? undefined : onRemove}
    >
      <XIcon />
    </Button>
  );

  if (!inUse) return button;

  return (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipContent>{IN_USE_MESSAGE}</TooltipContent>
    </Tooltip>
  );
};

const StatusFormFields = () => {
  const { control, getValues } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "children",
  });
  const hasChildren = fields.length > 0;

  const addChild = () => {
    const children = getValues("children");
    const lastChild = children[children.length - 1];
    append({
      name: "",
      description: "",
      is_closed: lastChild?.is_closed ?? false,
      is_won: lastChild?.is_won ?? false,
    });
  };

  return (
    <>
      <FormInput name="name" label="Name" required autoFocus />
      <FormTextarea
        name="description"
        label="Description"
        placeholder="What this status means and when to use it"
        required
      />
      {!hasChildren && (
        <div
          role="group"
          aria-label="Status flags"
          className="tw:flex tw:flex-col tw:gap-5"
        >
          <StatusFlagToggles namePrefix="" />
        </div>
      )}
      <FormSection title="Children" />
      {fields.map((field, index) => (
        <div
          key={field.id}
          role="group"
          aria-label={`Child ${index + 1}`}
          className="tw:flex tw:flex-col tw:gap-2"
        >
          <div className="tw:flex tw:items-end tw:gap-2">
            <div className="tw:flex-1">
              <FormInput
                name={`children.${index}.name`}
                aria-label={`Child ${index + 1} name`}
              />
            </div>
            <RemoveChildButton
              inUse={field.in_use}
              onRemove={() => remove(index)}
            />
          </div>
          <FormTextarea
            name={`children.${index}.description`}
            aria-label={`Child ${index + 1} description`}
            placeholder="What this child status means and when to use it"
          />
          <StatusFlagToggles namePrefix={`children.${index}.`} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addChild}>
        <PlusIcon data-icon="inline-start" />
        Add Child
      </Button>
    </>
  );
};

function RevenueOpportunityStatusForm({ model, onSuccess }) {
  const {
    data: status,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["revenue-opportunity-status", model?.id],
    queryFn: () =>
      RevenueOpportunityService.getRevenueOpportunityStatus(model.id),
    enabled: !!model?.id,
    gcTime: 0,
  });

  const initialValues = useMemo(() => {
    if (!model?.id) {
      return {
        name: "",
        description: "",
        is_closed: false,
        is_won: false,
        children: [],
      };
    }
    return (
      status && {
        name: status.name,
        description: status.description ?? "",
        is_closed: status.is_closed ?? false,
        is_won: status.is_won ?? false,
        children: status.children.map(child => ({
          id: child.id,
          name: child.name,
          description: child.description ?? "",
          in_use: child.in_use ?? false,
          is_closed: child.is_closed ?? false,
          is_won: child.is_won ?? false,
        })),
      }
    );
  }, [model?.id, status]);

  const handleSubmit = async values => {
    const data = {
      name: values.name,
      description: values.description,
      children: values.children.map(toChildPayload),
    };
    if (values.children.length === 0) {
      data.is_closed = values.is_closed;
      data.is_won = values.is_won;
    }
    if (model?.id) {
      await RevenueOpportunityService.editRevenueOpportunityStatus(
        model.id,
        data
      );
    } else {
      await RevenueOpportunityService.createRevenueOpportunityStatus(data);
    }
    toast.success("Revenue Opportunity Status saved");
  };

  if (isError) {
    return (
      <p className="tw:text-destructive">
        Failed to load Revenue Opportunity Status
      </p>
    );
  }
  if (isLoading || !initialValues) {
    return <Spinner />;
  }

  return (
    <BaseForm
      schema={schema}
      defaultValues={initialValues}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
    >
      <StatusFormFields />
    </BaseForm>
  );
}

export default RevenueOpportunityStatusForm;
